// src/components/module/ventas/PagoCuotaModal.tsx
// Modal para abonar a cuotas de crédito con distribución automática del excedente
import React, { useContext, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Stack,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon, Payment, AccountBalanceWallet,
  CalendarToday, ArrowForward, CheckCircleOutline,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { es } from "date-fns/locale";
import { CreditoApi, CuotaCredito, RespuestaPagoCuota } from "../../../services/api/creditoApi";
import { formatMoney, formatDate } from "../../../utils/format";
import { DarkModeContext } from "../../../context/darkModeContext";

// ── Paleta Notion/Linear ──
const P = {
  light: {
    bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F",
    textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0",
    accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB",
    warning: "#D9730D", warningBg: "#FADEC9", error: "#E03E3E", errorBg: "#FBE4E4",
    purple: "#6940A5", purpleBg: "#EAE4F2",
    hover: "rgba(55,53,47,0.04)",
  },
  dark: {
    bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6",
    textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444",
    accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A",
    warning: "#C77D48", warningBg: "#2E2215", error: "#D44C47", errorBg: "#3D2020",
    purple: "#9A6DD7", purpleBg: "#2A2040",
    hover: "rgba(255,255,255,0.04)",
  },
};

interface PagoCuotaModalProps {
  open: boolean;
  onClose: () => void;
  cuota: CuotaCredito;
  /** Todas las cuotas del crédito (para calcular distribución preview) */
  todasCuotas: CuotaCredito[];
  onPagoExitoso: (respuesta: RespuestaPagoCuota) => void;
  creditoNombre?: string;
}

const thStyle = {
  fontSize: "0.72rem", fontWeight: 600, textTransform: "uppercase" as const,
  letterSpacing: "0.04em", py: 1,
};

export const PagoCuotaModal: React.FC<PagoCuotaModalProps> = ({
  open, onClose, cuota, todasCuotas, onPagoExitoso, creditoNombre,
}) => {
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const [valorPagado, setValorPagado] = useState(cuota.ValorCuota.toString());
  const [fechaPago, setFechaPago] = useState<Date>(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Cuotas pendientes desde la seleccionada (para preview)
  const cuotasPendientesDesde = useMemo(() => {
    const pendientes = todasCuotas
      .filter((c) => c.Estado !== "pagada")
      .sort((a, b) => a.NumeroCuota - b.NumeroCuota);
    const idx = pendientes.findIndex((c) => c.Id === cuota.Id);
    return idx >= 0 ? pendientes.slice(idx) : [];
  }, [todasCuotas, cuota.Id]);

  // Saldo total pendiente desde la cuota seleccionada
  const saldoPendienteTotal = useMemo(() => {
    return cuotasPendientesDesde.reduce((sum, c) => {
      return sum + (Number(c.ValorCuota) - Number(c.ValorPagado || 0));
    }, 0);
  }, [cuotasPendientesDesde]);

  // Preview de distribución
  const distribucionPreview = useMemo(() => {
    const monto = parseFloat(valorPagado);
    if (isNaN(monto) || monto <= 0) return [];

    let disponible = monto;
    const items: { cuota: CuotaCredito; montoAplicado: number; completamentePagada: boolean }[] = [];

    for (const c of cuotasPendientesDesde) {
      if (disponible <= 0) break;
      const saldoC = Number(c.ValorCuota) - Number(c.ValorPagado || 0);
      if (saldoC <= 0) continue;
      const aplicado = Math.min(disponible, saldoC);
      items.push({
        cuota: c,
        montoAplicado: aplicado,
        completamentePagada: aplicado >= saldoC,
      });
      disponible -= aplicado;
    }

    return items;
  }, [valorPagado, cuotasPendientesDesde]);

  const excedentePreview = useMemo(() => {
    const monto = parseFloat(valorPagado);
    if (isNaN(monto) || monto <= 0) return 0;
    return Math.max(0, monto - saldoPendienteTotal);
  }, [valorPagado, saldoPendienteTotal]);

  const handleClose = () => {
    setValorPagado(cuota.ValorCuota.toString());
    setFechaPago(new Date());
    setError("");
    onClose();
  };

  const handleConfirmarPago = async () => {
    const valor = parseFloat(valorPagado);
    if (isNaN(valor) || valor <= 0) {
      setError("Ingresa un valor válido mayor a 0");
      return;
    }
    if (valor > saldoPendienteTotal) {
      setError(`El monto excede el saldo pendiente (${formatMoney(saldoPendienteTotal)})`);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const fechaStr = fechaPago.toISOString().split("T")[0];
      const respuesta = await CreditoApi.pagarCuota(cuota.Id, valor, fechaStr);
      onPagoExitoso(respuesta);
    } catch (err: any) {
      setError(err.response?.data?.message || "Error al registrar el pago");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{
        sx: { borderRadius: "14px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden" },
      }}>
        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${p.border}` }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 42, height: 42, borderRadius: "10px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", color: p.accent }}>
              <Payment sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: p.text }}>
                Abonar a Crédito
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: p.textT, mt: 0.2 }}>
                Desde cuota #{cuota.NumeroCuota}{creditoNombre ? ` · ${creditoNombre}` : ""}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} sx={{ width: 34, height: 34, borderRadius: "8px", color: p.textT, "&:hover": { bgcolor: p.bgT } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, bgcolor: p.bgS }}>
          {/* Info de saldo */}
          <Box sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <AccountBalanceWallet sx={{ fontSize: 16, color: p.accent }} />
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 650, color: p.text }}>Saldo Pendiente</Typography>
            </Stack>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: "0.82rem", color: p.textS }}>Cuota #{cuota.NumeroCuota}</Typography>
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: p.text, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {formatMoney(cuota.ValorCuota)}
                </Typography>
              </Stack>
              {cuotasPendientesDesde.length > 1 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: "0.82rem", color: p.textS }}>
                    {cuotasPendientesDesde.length} cuotas pendientes (#{cuota.NumeroCuota}–#{cuotasPendientesDesde[cuotasPendientesDesde.length - 1].NumeroCuota})
                  </Typography>
                  <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: p.accent, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {formatMoney(saldoPendienteTotal)}
                  </Typography>
                </Stack>
              )}
              <Divider sx={{ borderColor: p.border }} />
              <Box sx={{ p: 1.5, borderRadius: "8px", bgcolor: p.accentBg, border: `1px solid ${alpha(p.accent, 0.2)}` }}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: p.accent, lineHeight: 1.5 }}>
                  💡 El excedente se aplica automáticamente a las siguientes cuotas en orden
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Formulario */}
          <Stack spacing={2.5}>
            {/* Monto a abonar */}
            <Box>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.text, textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>
                Monto a abonar
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={valorPagado}
                onChange={(e) => { setValorPagado(e.target.value); setError(""); }}
                inputProps={{ min: 0, step: 100 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: p.textT }}>$</Typography>
                    </InputAdornment>
                  ),
                  sx: {
                    bgcolor: p.bg, borderRadius: "8px", fontSize: "1.1rem", fontWeight: 700,
                    color: p.text, fontFamily: "'IBM Plex Mono', monospace",
                    "& fieldset": { borderColor: p.border },
                    "&:hover fieldset": { borderColor: p.borderH },
                    "&.Mui-focused fieldset": { borderColor: p.accent },
                  },
                }}
                sx={{ "& .MuiInputBase-input": { py: 1.5 } }}
                placeholder="0"
              />
            </Box>

            {/* Fecha de pago */}
            <Box>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.text, textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>
                Fecha de pago
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <DatePicker
                  value={fechaPago}
                  onChange={(val) => { if (val) setFechaPago(val); }}
                  maxDate={new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        "& .MuiInputBase-root": {
                          bgcolor: p.bg, borderRadius: "8px", fontSize: "0.9rem",
                          color: p.text,
                          "& fieldset": { borderColor: p.border },
                          "&:hover fieldset": { borderColor: p.borderH },
                          "&.Mui-focused fieldset": { borderColor: p.accent },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            {/* Preview de distribución */}
            {distribucionPreview.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 1.5 }}>
                  Distribución previa
                </Typography>
                <TableContainer sx={{ borderRadius: "8px", border: `1px solid ${p.border}`, bgcolor: p.bg, maxHeight: 200 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...thStyle, color: p.textT, bgcolor: p.bgS }}>Cuota</TableCell>
                        <TableCell sx={{ ...thStyle, color: p.textT, bgcolor: p.bgS }} align="right">Saldo</TableCell>
                        <TableCell sx={{ ...thStyle, color: p.accent, bgcolor: p.bgS }} align="right">Se aplica</TableCell>
                        <TableCell sx={{ ...thStyle, color: p.textT, bgcolor: p.bgS }} align="center">Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {distribucionPreview.map((item) => (
                        <TableRow key={item.cuota.Id} sx={{ "&:hover": { bgcolor: p.hover } }}>
                          <TableCell sx={{ py: 0.8, fontSize: "0.84rem", fontWeight: 600, borderBottom: `1px solid ${p.border}` }}>
                            #{item.cuota.NumeroCuota}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.8, fontSize: "0.82rem", fontFamily: "'IBM Plex Mono', monospace", color: p.textS, borderBottom: `1px solid ${p.border}` }}>
                            {formatMoney(Number(item.cuota.ValorCuota) - Number(item.cuota.ValorPagado || 0))}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.8, fontSize: "0.84rem", fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color: p.accent, borderBottom: `1px solid ${p.border}` }}>
                            {formatMoney(item.montoAplicado)}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.8, borderBottom: `1px solid ${p.border}` }}>
                            {item.completamentePagada ? (
                              <CheckCircleOutline sx={{ fontSize: 16, color: p.success }} />
                            ) : (
                              <Typography sx={{ fontSize: "0.72rem", color: p.warning, fontWeight: 600 }}>Parcial</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {excedentePreview > 0 && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 1, borderBottom: "none" }}>
                            <Typography sx={{ fontSize: "0.78rem", color: p.error, textAlign: "center", fontWeight: 600 }}>
                              ⚠️ Excedente: {formatMoney(excedentePreview)} — supera el saldo pendiente
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {distribucionPreview.length > 1 && (
                  <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5} sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: "0.75rem", color: p.textT }}>
                      {distribucionPreview.length} cuota{distribucionPreview.length > 1 ? "s" : ""} afectada{distribucionPreview.length > 1 ? "s" : ""}
                    </Typography>
                    <ArrowForward sx={{ fontSize: 14, color: p.textT }} />
                  </Stack>
                )}
              </Box>
            )}

            {/* Error */}
            {error && (
              <Typography sx={{ fontSize: "0.82rem", color: p.error, textAlign: "center", bgcolor: p.errorBg, p: 1.5, borderRadius: "8px" }}>
                {error}
              </Typography>
            )}

            {/* Botones */}
            <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClose}
                sx={{
                  py: 1.2, borderRadius: "8px", fontSize: "0.88rem", fontWeight: 600,
                  color: p.textS, borderColor: p.border,
                  "&:hover": { bgcolor: p.bgT, borderColor: p.borderH },
                }}
              >
                Cancelar
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleConfirmarPago}
                disabled={loading || distribucionPreview.length === 0}
                sx={{
                  py: 1.2, borderRadius: "8px", fontSize: "0.88rem", fontWeight: 600,
                  bgcolor: p.accent, color: "#FFF",
                  "&:hover": { bgcolor: darkMode ? "#4A8CBA" : "#1B6FC2" },
                  "&:disabled": { bgcolor: p.bgT, color: p.textT },
                }}
              >
                {loading ? (
                  <CircularProgress size={20} sx={{ color: "#FFF" }} />
                ) : (
                  `Confirmar Abono${distribucionPreview.length > 1 ? ` (${distribucionPreview.length} cuotas)` : ""}`
                )}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
  );
};

export default PagoCuotaModal;
